import React, { useState } from "react";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Checkbox } from "../ui/checkbox";
import { useToast } from "../../hooks/use-toast";
import { 
  CreditCard, 
  Phone, 
  Shield, 
  Lock,
  Building,
  MapPin,
  DollarSign,
  Check
} from "lucide-react";

const PaymentForm = ({ orderData, onPaymentSuccess, onCancel }) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("card");
  const [saveCard, setSaveCard] = useState(false);

  const [cardData, setCardData] = useState({
    cardNumber: "",
    cardholderName: "",
    expiryMonth: "",
    expiryYear: "",
    cvv: "",
    billingAddress: {
      street: "",
      city: "",
      state: "",
      zipCode: "",
      country: "US"
    }
  });

  const [phonePayment, setPhonePayment] = useState({
    phone: "",
    preferredTime: "",
    notes: ""
  });

  const handleCardChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setCardData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setCardData(prev => ({ ...prev, [field]: value }));
    }
  };

  const handlePhoneChange = (field, value) => {
    setPhonePayment(prev => ({ ...prev, [field]: value }));
  };

  const formatCardNumber = (value) => {
    // Remove all non-digit characters
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    
    // Add spaces every 4 digits
    const matches = v.match(/\d{4,16}/g);
    const match = matches && matches[0] || '';
    const parts = [];
    
    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }
    
    if (parts.length) {
      return parts.join(' ');
    } else {
      return v;
    }
  };

  const formatCVV = (value) => {
    return value.replace(/[^0-9]/g, '').slice(0, 4);
  };

  const getCurrentYear = () => new Date().getFullYear();
  const getYears = () => {
    const currentYear = getCurrentYear();
    const years = [];
    for (let i = 0; i < 15; i++) {
      years.push(currentYear + i);
    }
    return years;
  };

  const validateCard = () => {
    const { cardNumber, cardholderName, expiryMonth, expiryYear, cvv, billingAddress } = cardData;
    
    if (!cardNumber || cardNumber.replace(/\s/g, '').length < 13) {
      return "Please enter a valid card number";
    }
    if (!cardholderName) {
      return "Please enter the cardholder's name";
    }
    if (!expiryMonth || !expiryYear) {
      return "Please enter the expiration date";
    }
    if (!cvv || cvv.length < 3) {
      return "Please enter a valid CVV";
    }
    if (!billingAddress.street || !billingAddress.city || !billingAddress.state || !billingAddress.zipCode) {
      return "Please complete the billing address";
    }
    
    return null;
  };

  const processPayment = async () => {
    setLoading(true);
    
    try {
      if (paymentMethod === "card") {
        const validationError = validateCard();
        if (validationError) {
          toast({
            title: "Validation Error",
            description: validationError,
            variant: "destructive"
          });
          return;
        }
      } else if (paymentMethod === "phone") {
        if (!phonePayment.phone) {
          toast({
            title: "Phone Required",
            description: "Please enter your phone number for over-the-phone payment",
            variant: "destructive"
          });
          return;
        }
      }

      // Mock payment processing
      await new Promise(resolve => setTimeout(resolve, 3000));
      
      toast({
        title: "Payment Successful",
        description: "Your business card order has been processed successfully!"
      });
      
      onPaymentSuccess({
        method: paymentMethod,
        transactionId: `txn_${Date.now()}`,
        amount: orderData.amount
      });
      
    } catch (error) {
      toast({
        title: "Payment Failed",
        description: "There was an error processing your payment. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Order Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <DollarSign className="h-5 w-5 text-emerald-600" />
            <span>Order Summary</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">Business Card Design</span>
              <span className="font-semibold">$29.99</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">HD PDF Generation</span>
              <span className="font-semibold">$19.99</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Processing Fee</span>
              <span className="font-semibold">$2.99</span>
            </div>
            <div className="border-t pt-3 mt-3">
              <div className="flex justify-between text-lg font-bold">
                <span>Total</span>
                <span className="text-emerald-600">$52.97</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Payment Methods */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <CreditCard className="h-5 w-5 text-blue-600" />
            <span>Payment Method</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs value={paymentMethod} onValueChange={setPaymentMethod}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="card" className="flex items-center space-x-2">
                <CreditCard className="h-4 w-4" />
                <span>Credit/Debit Card</span>
              </TabsTrigger>
              <TabsTrigger value="phone" className="flex items-center space-x-2">
                <Phone className="h-4 w-4" />
                <span>Over-the-Phone</span>
              </TabsTrigger>
            </TabsList>

            {/* Credit Card Payment */}
            <TabsContent value="card" className="space-y-6 mt-6">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="cardNumber">Card Number *</Label>
                  <div className="relative">
                    <CreditCard className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input
                      id="cardNumber"
                      placeholder="1234 5678 9012 3456"
                      value={cardData.cardNumber}
                      onChange={(e) => handleCardChange("cardNumber", formatCardNumber(e.target.value))}
                      className="pl-10"
                      maxLength={19}
                      required
                    />
                  </div>
                  <p className="text-xs text-gray-500">The long number on the front of the card</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="cardholderName">Cardholder's Name *</Label>
                  <Input
                    id="cardholderName"
                    placeholder="John Smith"
                    value={cardData.cardholderName}
                    onChange={(e) => handleCardChange("cardholderName", e.target.value.toUpperCase())}
                    required
                  />
                  <p className="text-xs text-gray-500">Name as it appears on the card</p>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="expiryMonth">Exp. Month *</Label>
                  <Select value={cardData.expiryMonth} onValueChange={(value) => handleCardChange("expiryMonth", value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="MM" />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
                        <SelectItem key={month} value={month.toString().padStart(2, '0')}>
                          {month.toString().padStart(2, '0')}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="expiryYear">Exp. Year *</Label>
                  <Select value={cardData.expiryYear} onValueChange={(value) => handleCardChange("expiryYear", value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="YYYY" />
                    </SelectTrigger>
                    <SelectContent>
                      {getYears().map((year) => (
                        <SelectItem key={year} value={year.toString()}>
                          {year}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="cvv">CVV/CVC *</Label>
                  <div className="relative">
                    <Shield className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input
                      id="cvv"
                      placeholder="123"
                      value={cardData.cvv}
                      onChange={(e) => handleCardChange("cvv", formatCVV(e.target.value))}
                      className="pl-10"
                      maxLength={4}
                      required
                    />
                  </div>
                  <p className="text-xs text-gray-500">3-4 digit code on back of card</p>
                </div>
              </div>

              {/* Billing Address */}
              <div className="space-y-4">
                <div className="flex items-center space-x-2">
                  <Building className="h-4 w-4 text-gray-600" />
                  <Label className="text-base font-semibold">Billing Address</Label>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="street">Street Address *</Label>
                  <Input
                    id="street"
                    placeholder="123 Main Street"
                    value={cardData.billingAddress.street}
                    onChange={(e) => handleCardChange("billingAddress.street", e.target.value)}
                    required
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="city">City *</Label>
                    <Input
                      id="city"
                      placeholder="New York"
                      value={cardData.billingAddress.city}
                      onChange={(e) => handleCardChange("billingAddress.city", e.target.value)}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="state">State *</Label>
                    <Input
                      id="state"
                      placeholder="NY"
                      value={cardData.billingAddress.state}
                      onChange={(e) => handleCardChange("billingAddress.state", e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="zipCode">ZIP Code *</Label>
                    <Input
                      id="zipCode"
                      placeholder="10001"
                      value={cardData.billingAddress.zipCode}
                      onChange={(e) => handleCardChange("billingAddress.zipCode", e.target.value)}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="country">Country *</Label>
                    <Select value={cardData.billingAddress.country} onValueChange={(value) => handleCardChange("billingAddress.country", value)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="US">United States</SelectItem>
                        <SelectItem value="CA">Canada</SelectItem>
                        <SelectItem value="GB">United Kingdom</SelectItem>
                        <SelectItem value="AU">Australia</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Save Card Option */}
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="saveCard"
                  checked={saveCard}
                  onCheckedChange={setSaveCard}
                />
                <Label htmlFor="saveCard" className="text-sm">
                  Save this card for future purchases
                </Label>
              </div>
            </TabsContent>

            {/* Over-the-Phone Payment */}
            <TabsContent value="phone" className="space-y-6 mt-6">
              <div className="bg-blue-50 p-6 rounded-lg">
                <div className="flex items-start space-x-3">
                  <Phone className="h-6 w-6 text-blue-600 mt-1" />
                  <div>
                    <h3 className="font-semibold text-blue-900 mb-2">Over-the-Phone Payment</h3>
                    <p className="text-sm text-blue-700 mb-4">
                      Our secure payment team will call you to collect your payment information. 
                      This ensures maximum security for your financial data.
                    </p>
                    <div className="space-y-2 text-sm text-blue-600">
                      <p>✓ Secure and encrypted call</p>
                      <p>✓ No card details stored online</p>
                      <p>✓ PCI DSS compliant processing</p>
                      <p>✓ Available Monday - Friday, 9 AM - 6 PM EST</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="phoneNumber">Phone Number *</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input
                      id="phoneNumber"
                      type="tel"
                      placeholder="+1 (555) 123-4567"
                      value={phonePayment.phone}
                      onChange={(e) => handlePhoneChange("phone", e.target.value)}
                      className="pl-10"
                      required
                    />
                  </div>
                  <p className="text-xs text-gray-500">We'll call this number to process your payment</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="preferredTime">Preferred Call Time</Label>
                  <Select value={phonePayment.preferredTime} onValueChange={(value) => handlePhoneChange("preferredTime", value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select preferred time" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="morning">Morning (9 AM - 12 PM)</SelectItem>
                      <SelectItem value="afternoon">Afternoon (12 PM - 3 PM)</SelectItem>
                      <SelectItem value="evening">Evening (3 PM - 6 PM)</SelectItem>
                      <SelectItem value="anytime">Anytime</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="notes">Special Instructions (Optional)</Label>
                  <Input
                    id="notes"
                    placeholder="Any specific instructions for the call..."
                    value={phonePayment.notes}
                    onChange={(e) => handlePhoneChange("notes", e.target.value)}
                  />
                </div>
              </div>
            </TabsContent>
          </Tabs>

          {/* Security Notice */}
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <div className="flex items-start space-x-3">
              <Lock className="h-5 w-5 text-gray-600 mt-0.5" />
              <div className="text-sm text-gray-600">
                <p className="font-semibold mb-1">Your payment is secure</p>
                <p>We use industry-standard SSL encryption to protect your information. Your card details are never stored on our servers.</p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-4 mt-8">
            <Button
              onClick={processPayment}
              disabled={loading}
              className="flex-1 bg-gradient-to-r from-emerald-500 to-blue-600 hover:from-emerald-600 hover:to-blue-700 text-white py-3"
            >
              {loading ? (
                "Processing..."
              ) : paymentMethod === "card" ? (
                <>
                  <Lock className="mr-2 h-4 w-4" />
                  Pay $52.97 Securely
                </>
              ) : (
                <>
                  <Phone className="mr-2 h-4 w-4" />
                  Schedule Payment Call
                </>
              )}
            </Button>
            
            <Button variant="outline" onClick={onCancel} className="px-8">
              Cancel
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentForm;